import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useToast } from '../../../hooks/use-toast';
import { Loader2, Save, Plus, Trash2, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import BackButton from '../../../components/admin/BackButton';
import AutoTranslateIndicator from '../../../components/admin/AutoTranslateIndicator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyLocation = () => ({
  name_pt: '',
  role_pt: '',
  address: '',
  phone: '',
  email: '',
});

const FooterForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    whatsapp: '',
    company_links_label_pt: '',
    services_links_label_pt: '',
    copyright_pt: '',
    tagline_pt: '',
    social_linkedin: '',
    social_instagram: '',
    social_twitter: '',
    social_facebook: '',
    locations: [],
  });

  useEffect(() => {
    loadFooterData();
  }, []);

  const loadFooterData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/homepage-ml/contact?lang=raw`);
      const d = response.data || {};
      setFormData({
        email: d.email || '',
        phone: d.phone || '',
        whatsapp: d.whatsapp || '',
        company_links_label_pt: d.company_links_label_pt || '',
        services_links_label_pt: d.services_links_label_pt || '',
        copyright_pt: d.copyright_pt || '',
        tagline_pt: d.tagline_pt || '',
        social_linkedin: d.social_linkedin || '',
        social_instagram: d.social_instagram || '',
        social_twitter: d.social_twitter || '',
        social_facebook: d.social_facebook || '',
        locations: Array.isArray(d.locations) && d.locations.length
          ? d.locations.map((loc) => ({
              name_pt: loc.name_pt || loc.name || '',
              role_pt: loc.role_pt || '',
              address: loc.address || '',
              phone: loc.phone || '',
              email: loc.email || '',
            }))
          : [emptyLocation()],
      });
    } catch (err) {
      console.error('Failed to load footer/contact:', err);
      toast({ variant: 'destructive', title: 'Erro', description: 'Falha ao carregar dados' });
    }
    setLoading(false);
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateLocation = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) => (i === idx ? { ...loc, [field]: value } : loc)),
    }));
  };

  const addLocation = () => {
    setFormData((prev) => ({ ...prev, locations: [...prev.locations, emptyLocation()] }));
  };

  const removeLocation = (idx) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/homepage-ml/contact`, formData);
      toast({
        title: 'Footer guardado',
        description: 'Traduções EN/FR/AR/ZH a serem geradas em segundo plano.',
      });
    } catch (err) {
      console.error(err);
      toast({ variant: 'destructive', title: 'Erro', description: 'Falha ao guardar' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="footer-admin-form">
      <div className="container mx-auto px-6 max-w-4xl">
        <BackButton />

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Footer & Contact Info</CardTitle>
            <CardDescription>
              Controla o rodapé do site e as informações de contacto em todas as páginas públicas.
              Edite em Português — traduções para EN / FR / AR / ZH são geradas automaticamente ao gravar.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-8">

              {/* PRIMARY CONTACT */}
              <section className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#023E8A]">Contacto Principal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email Principal
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="info@grupobmp.com"
                      data-testid="footer-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Telefone Principal
                    </label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="+971 4 XXX XXXX"
                      data-testid="footer-phone-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4 text-green-600" /> WhatsApp Business
                    </label>
                    <input
                      type="text"
                      value={formData.whatsapp}
                      onChange={(e) => updateField('whatsapp', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="+971504710240"
                      data-testid="footer-whatsapp-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Formato internacional (ex: +971504710240). Aparecerá como botão flutuante em todas as páginas públicas.
                    </p>
                  </div>
                </div>
              </section>

              {/* TAGLINE & LABELS (PT only — auto-translate on save) */}
              <section className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-1 text-[#023E8A]">Textos do Rodapé (PT)</h3>
                <p className="text-xs text-gray-500 mb-4">Traduções automáticas: EN / FR / AR / ZH</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Tagline</label>
                    <textarea
                      value={formData.tagline_pt}
                      onChange={(e) => updateField('tagline_pt', e.target.value)}
                      rows={2}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="Excelência em arquitectura empresarial e gestão internacional desde 2008."
                      data-testid="footer-tagline-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Título Coluna "Empresa"</label>
                      <input
                        type="text"
                        value={formData.company_links_label_pt}
                        onChange={(e) => updateField('company_links_label_pt', e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                        placeholder="Empresa"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Título Coluna "Serviços"</label>
                      <input
                        type="text"
                        value={formData.services_links_label_pt}
                        onChange={(e) => updateField('services_links_label_pt', e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                        placeholder="Serviços"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Copyright</label>
                    <input
                      type="text"
                      value={formData.copyright_pt}
                      onChange={(e) => updateField('copyright_pt', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="© 2026 Grupo Bruno Miguel Pegado. Todos os direitos reservados."
                    />
                  </div>
                </div>
              </section>

              {/* LOCATIONS / OFFICES */}
              <section className="border-b pb-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-[#023E8A]">Escritórios / Localizações</h3>
                    <p className="text-xs text-gray-500">Apresentados no rodapé e na página de Contacto.</p>
                  </div>
                  <Button type="button" variant="outline" onClick={addLocation} data-testid="footer-add-location-btn">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.locations.map((loc, idx) => (
                    <div key={idx} className="border rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-[#00B4D8]" />
                          Escritório {idx + 1}
                        </span>
                        {formData.locations.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLocation(idx)}
                            className="text-red-600 hover:text-red-800"
                            data-testid={`footer-remove-location-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Nome / Cidade (PT)</label>
                          <input
                            type="text"
                            value={loc.name_pt}
                            onChange={(e) => updateLocation(idx, 'name_pt', e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                            placeholder="Dubai - Sede"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Função (PT)</label>
                          <input
                            type="text"
                            value={loc.role_pt}
                            onChange={(e) => updateLocation(idx, 'role_pt', e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                            placeholder="Sede Global"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium mb-1">Morada</label>
                          <input
                            type="text"
                            value={loc.address}
                            onChange={(e) => updateLocation(idx, 'address', e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                            placeholder="Dubai Silicon Oasis, DDP, Building A2, Office 101"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Telefone</label>
                          <input
                            type="text"
                            value={loc.phone}
                            onChange={(e) => updateLocation(idx, 'phone', e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                            placeholder="+971 4 XXX XXXX"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Email</label>
                          <input
                            type="email"
                            value={loc.email}
                            onChange={(e) => updateLocation(idx, 'email', e.target.value)}
                            className="w-full px-3 py-2 border rounded"
                            placeholder="dubai@grupobmp.com"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* SOCIAL MEDIA */}
              <section>
                <h3 className="text-lg font-semibold mb-4 text-[#023E8A]">Redes Sociais</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">LinkedIn URL</label>
                    <input
                      type="url"
                      value={formData.social_linkedin}
                      onChange={(e) => updateField('social_linkedin', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="https://linkedin.com/company/grupobmp"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Instagram URL</label>
                    <input
                      type="url"
                      value={formData.social_instagram}
                      onChange={(e) => updateField('social_instagram', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="https://instagram.com/grupobrunomiguelpegado"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Facebook URL</label>
                    <input
                      type="url"
                      value={formData.social_facebook}
                      onChange={(e) => updateField('social_facebook', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="https://facebook.com/grupobmp"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Twitter / X URL</label>
                    <input
                      type="url"
                      value={formData.social_twitter}
                      onChange={(e) => updateField('social_twitter', e.target.value)}
                      className="w-full px-4 py-2 border rounded-lg"
                      placeholder="https://twitter.com/grupobmp"
                    />
                  </div>
                </div>
              </section>

              {/* ACTIONS */}
              <div className="space-y-4 pt-6 border-t">
                <AutoTranslateIndicator />
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => navigate('/admin/homepage')}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-[#00B4D8] hover:bg-[#0096c7]"
                    data-testid="footer-save-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        A guardar...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FooterForm;
